import React, { useEffect, useRef, useState } from 'react';
import { Column } from 'primereact/column';
import { promocodeService } from 'services/promocode.service';
import { Paginator } from 'primereact/paginator';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Card } from 'primereact/card';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { getSortIcon, handleFilters, showTostify } from 'utils/common-functions';
import './style.scss';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import CustomToast from 'views/components/form-inputs/alert';
import { _Object } from 'utils/interfaces';
import { Skeleton } from 'primereact/skeleton';
import { InputField } from 'views/components/form-inputs';

const Excel = () => {
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<_Object>({})
  const [promocodes, setPromocodes] = useState([]);
  const [visible, setVisible] = useState(false);
  const [generatedData, setGeneratedData] = useState<any>([]);
  const [generatedVisible, setGeneratedVisible] = useState(false);
  const toastRef = useRef(null);
  const [filterData, setFilterData] = useState<_Object>({
    page: 1,
    per_page: 10,
    q: "",
    sort: '-created_at',
  })
  const fetchPromocodes = async () => {
    try {
      setLoading(true);
      const data = await promocodeService.getPromocodesExcel(filterData);
      setPromocodes(data.items);
      setPagination(data.pagination);
    } catch (error) {
      console.error('Error fetching promocodes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromocodes();
  }, [filterData]);

  const formik = useFormik({
    initialValues: {
      coupon_counts: '',
      coupons_per_excel: ''
    },
    validationSchema: Yup.object({
      coupon_counts: Yup.string().required('Coupon counts is required'),
      coupons_per_excel: Yup.string().required('Coupons per excel is required'),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const data = await promocodeService.generatePromocodesExcel(values);
        if (!data.error) {
          setVisible(false);
          showTostify(toastRef, 'success', data.message);

          const totalCoupons = parseInt(values.coupon_counts);
          const couponsPerExcel = parseInt(values.coupons_per_excel);
          const totalExcelFiles = Math.ceil(totalCoupons / couponsPerExcel);

          const newGeneratedData = data.data.map((excelNumber: any, index: number) => {
            const isLastFile = index === totalExcelFiles - 1;
            return {
              excel_number: parseInt(excelNumber),
              promocodes_count: isLastFile ? totalCoupons % couponsPerExcel || couponsPerExcel : couponsPerExcel,
              coupons_per_excel: values.coupons_per_excel
            };
          });

          setGeneratedData((prevData: any) => [...prevData, ...newGeneratedData]);
          setGeneratedVisible(true);
          formik.resetForm();
          fetchPromocodes();
        } else {
          showTostify(toastRef, 'error', data.message);
        }
      } catch (err) {
        console.error('Error generating promocodes:', err);
      } finally {
        setLoading(false);
      }
    },
  });


  const handleDownload = async (excelNumber: any) => {
    try {
      const response = await promocodeService.downloadExcel({ excel_number: excelNumber });

      if (response && response.data && response.data.file) {
        const fileUrl = response.data.file;
        const link: any = document.createElement('a');
        link.href = fileUrl;
        link.setAttribute('download', `promocodes_${excelNumber}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);

        showTostify(toastRef, 'success', 'Excel file downloaded successfully');
      } else {
        throw new Error('File URL not found in response');
      }
    } catch (error) {
      console.error('Error downloading Excel:', error);
      showTostify(toastRef, 'error', 'Error downloading Excel');
    }
  };

  // const actionBodyTemplate = (rowData: any) => {
  //   return (
  //     <div className="flex gap-4">
  //       <Button
  //         icon="pi pi-download"
  //         text
  //         className="h-auto w-auto bg-transparent p-0 border-noround inline"
  //         onClick={() => handleDownload(rowData.excel_number)}
  //         style={{ color: '#959CB6' }}
  //       />
  //     </div>
  //   );
  // };
  const onPageChange = (event: any) => {
    const currentPage = Math.floor(event.first / event.rows) + 1;

    setFilterData((prev) => ({
      ...prev,
      page: currentPage,
      per_page: event.rows,
    }));
  };

  const formikFilter = useFormik({
    initialValues: filterData,
    enableReinitialize: true,
    onSubmit: (values: _Object) => {
      setFilterData((prev) => ({
        ...prev,
        q: values.q.trim(),
        page: 1
      }));
    }
  });

  return (
    <div className="promocodeLisiting">
      <div className="flex justify-content-between align-items-center">
        <div className="p-inputgroup search-bar col-6">
          <form onSubmit={formikFilter.handleSubmit}>
            <div className='form-group mb-0 mr-2'>
              <Button type='submit' className='search-loading bg-transparent p-0 border-none'
                disabled={loading}>
                {loading && <i className="pi pi-spin pi-spinner"
                  style={{ fontSize: '1rem' }}
                >
                </i>}
              </Button>

              <IconField iconPosition="left">
                <InputIcon className="pi pi-search"> </InputIcon>
                <InputText placeholder="Search"
                  name='q'
                  onChange={formikFilter.handleChange}
                  onBlur={formikFilter.handleBlur}
                  value={formikFilter.values.q} />
              </IconField>

              {filterData.q && <Button type='button'
                onClick={() => {
                  setFilterData((prev) => ({
                    ...prev,
                    q: '',
                    page: 1
                  }))
                }}
                severity="secondary" text className='bg-transparent p-0 border-none absolute top-0 right-0 mr-3'>
                <span className="pi pi-times "> </span>
              </Button>}
            </div>
          </form>

          {pagination?.total_items > 0 &&
            <div className='total-items flex align-items-center text-sm'>
              Total items: {pagination.total_items || ""}
            </div>
          }
        </div>

        <CustomToast ref={toastRef} />

        <div className="col-4 text-right">
          <Button label="Create Excel" icon="pi pi-external-link" className="primary" onClick={() => {
            formik.resetForm()
            setVisible(true)
          }} />
        </div>
      </div>

      <Dialog
        header="Generate Excel"
        visible={visible}
        modal
        onHide={() => setVisible(false)}
      >
        <Card className='check-deals-code shadow-none border-primary-color'>
          <form onSubmit={formik.handleSubmit}>
            <InputField
              className="mb-3"
              name="coupon_counts"
              label="Number of coupons"
              type="number"
              required={true}
              value={formik.values.coupon_counts}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik?.touched.coupon_counts && formik.errors.coupon_counts}
            />
            <InputField
              className="mb-3"
              name="coupons_per_excel"
              label="Coupons per excel"
              type="number"
              required={true}
              value={formik.values.coupons_per_excel}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik?.touched.coupons_per_excel && formik.errors.coupons_per_excel}
            />

            <Button label="Generate" loading={loading} className='primary' type="submit" />
          </form>
        </Card>
      </Dialog>

      <Dialog header="Generated Excel" visible={generatedVisible} modal
        onHide={() => { setGeneratedVisible(false); setGeneratedData([]); }}
      >
        <DataTable value={generatedData} responsiveLayout="scroll">
          <Column
            header="#"
            body={(rowData, { rowIndex }) => rowIndex + 1}
            style={{ width: '50px' }}
          />
          <Column field="excel_number" header="Excel Number" />
          <Column field="promocodes_count" header="No. of Count" />
          {/* <Column field="coupons_per_excel" header="Coupons per Excel" /> */}
          <Column
            header="Actions"
            body={(rowData) => (
              <Button
                icon="pi pi-download"
                text
                className="h-auto w-auto bg-transparent p-0 border-noround inline"
                onClick={() => handleDownload(rowData.excel_number)}
                style={{ color: '#959CB6' }}
              />
            )}
          />
        </DataTable>
      </Dialog>

      <div className="col">
        <div className="card">
          <table className='w-full'>
            <thead>
              <tr>
                <th>#</th>
                <th>
                  <button
                    className="btn p-0 border-0"
                    onClick={() => { handleFilters('sort', filterData.sort === '-excel_number' ? 'excel_number' : '-excel_number', setFilterData) }}>
                    Excel Number
                    <i className={`pi ${getSortIcon(filterData.sort.includes('excel_number') ? filterData.sort : '')}`}></i>
                  </button>
                </th>
                <th>
                  <button
                    className="btn p-0 border-0"
                    onClick={() => { handleFilters('sort', filterData.sort === '-promocodes_count' ? 'promocodes_count' : '-promocodes_count', setFilterData) }}>
                    No. of Count
                    <i className={`pi ${getSortIcon(filterData.sort.includes('promocodes_count') ? filterData.sort : '')}`}></i>
                  </button>
                </th>
                <th>
                  <button
                    className="btn p-0 border-0"
                    onClick={() => { handleFilters('sort', filterData.sort === '-coupons_per_excel' ? 'coupons_per_excel' : '-coupons_per_excel', setFilterData) }}>
                    Coupons per Excel
                    <i className={`pi ${getSortIcon(filterData.sort.includes('coupons_per_excel') ? filterData.sort : '')}`}></i>
                  </button>
                </th>
                <th>Used Promocodes</th>
                <th>Printed</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading && Array.from({ length: 5 }).map((_, index) => (
                <tr key={index}>
                  <td><Skeleton height="2rem" borderRadius="1px" /></td>
                  <td><Skeleton height="2rem" borderRadius="4px" /></td>
                  <td><Skeleton height="2rem" borderRadius="4px" /></td>
                  <td><Skeleton height="2rem" borderRadius="4px" /></td>
                  <td><Skeleton height="2rem" borderRadius="4px" /></td>
                  <td><Skeleton height="2rem" borderRadius="4px" /></td>
                  <td><Skeleton height="2rem" borderRadius="4px" /></td>
                </tr>
              ))}
              {!loading && promocodes?.map((item: _Object, i: number) => (
                <tr>
                  <td>{((filterData.page - 1) * filterData.per_page) + (i + 1)}</td>
                  <td>{item?.excel_number || "-"}</td>
                  <td>{item.promocodes_count || '-'}</td>
                  <td>{item.coupons_per_excel || '-'}</td>
                  <td>{item.used_promocodes || '-'}</td>
                  <td>{item.printed || '-'}</td>
                  <td>
                    <div>
                      <Button
                        icon="pi pi-download"
                        text
                        className="h-auto w-auto bg-transparent p-0 border-noround inline"
                        onClick={() => handleDownload(item?.excel_number)}
                        style={{ color: '#959CB6' }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && promocodes?.length === 0 && <tr>
                <td colSpan={11}>No available options</td>
              </tr>
              }
            </tbody>
          </table>
        </div>

        {!loading && pagination.total_pages > 1 && (
          <Paginator
            first={(filterData.page - 1) * filterData.per_page}
            rows={filterData.per_page}
            onPageChange={onPageChange}
            totalRecords={pagination.total_items}
            rowsPerPageOptions={[10, 20, 30]}
          />
        )}
      </div>
    </div>
  );
};

export default Excel;
